// Public REST client (no auth) — consumed by Android runtime.

const API_BASE =
  (typeof window !== 'undefined' && (window as { __API_BASE__?: string }).__API_BASE__) ||
  (import.meta.env.VITE_API_BASE as string | undefined) ||
  'https://ar-impatras.collectiveaccess.gr/api/public';

export interface CardPublic {
  id: number;
  target_name: string;
  active_augmentation_id: string | null;
}

export interface AssetPublic {
  id: string;
  type: 'image' | 'audio' | 'model_3d';
  url: string;
  format: string | null;
  animations: Array<{ name: string; index: number }> | null;
  duration_ms: number | null;
  width_px: number | null;
  height_px: number | null;
}

export interface ElementPublic {
  id: string;
  asset_id: string;
  asset: AssetPublic;
  layer_order: number;
  pos_x: number; pos_y: number; pos_z: number;
  rot_x: number; rot_y: number; rot_z: number;
  scale: number; opacity: number;
  start_time_ms: number; end_time_ms: number;
  fade_in_ms: number; fade_out_ms: number;
  loop: boolean;
  animation_name: string | null;
  animation_speed: number;
  audio_spatial: boolean;
  audio_volume: number;
}

export interface AugmentationPublic {
  augmentation: {
    id: string;
    card_id: number;
    title_gr: string;
    title_en: string | null;
    total_duration_ms: number;
  };
  elements: ElementPublic[];
}

const etagCache = new Map<string, { etag: string; value: unknown }>();

async function getJson<T>(path: string): Promise<T> {
  const url = `${API_BASE}${path}`;
  const cached = etagCache.get(url);
  const headers: Record<string, string> = {};
  if (cached) headers['If-None-Match'] = cached.etag;

  const res = await fetch(url, { headers, cache: 'no-store' });

  if (res.status === 304 && cached) {
    return cached.value as T;
  }
  if (!res.ok) {
    throw new Error(`API ${res.status} on ${path}`);
  }

  const value = (await res.json()) as T;
  const etag = res.headers.get('ETag');
  if (etag) etagCache.set(url, { etag, value });
  return value;
}

export const api = {
  cards: () => getJson<CardPublic[]>('/cards'),
  augmentation: (id: string) => getJson<AugmentationPublic>(`/augmentations/${id}`),
  setBase: (base: string) => {
    if (typeof window !== 'undefined') {
      (window as { __API_BASE__?: string }).__API_BASE__ = base;
    }
  },
};
